import { useState, useRef, useEffect, useCallback } from 'react';
import { FaTimes, FaPlus, FaCog, FaUsers, FaCopy, FaTrash, FaChevronRight } from 'react-icons/fa';
import { FaIcon } from '@/utils/faIconMap';
import { useDroppable } from '@dnd-kit/core';
import '@/styles/ActionItemsStyles.scss';

/**
 * DroppableBoardItem - Makes a board item droppable for action items.
 * Declared at module scope (not inside BoardSidebar) so its component
 * identity is stable across renders — otherwise every BoardSidebar render
 * would remount each item, re-register useDroppable, and trigger an
 * infinite update loop via the hover-switch effect below.
 * Interdepartmental board (boardId === -1) is not droppable.
 */
const DroppableBoardItem = ({
    board,
    children,
    selectedBoardId,
    previewBoardId,
    draggedItemId,
    onBoardHover,
    sidebarOpen
}) => {
    const isInterdepartmental = board.boardId === -1;

    const { setNodeRef, isOver } = useDroppable({
        id: `board-drop-${board.boardId}`,
        data: {
            type: 'board',
            boardId: board.boardId
        },
        disabled: isInterdepartmental || !sidebarOpen
    });

    // Switch the rendered board while dragging using dnd-kit's own `isOver`
    // signal rather than DOM mouseenter/mouseleave events. During a dnd-kit
    // pointer drag the overlay portal intercepts mouse events, so DOM hover
    // events on sidebar items fire unreliably.
    useEffect(() => {
        if (!draggedItemId || isInterdepartmental || !onBoardHover) return;
        if (isOver) {
            onBoardHover(board.boardId);
        } else if (previewBoardId === board.boardId) {
            onBoardHover(null);
        }
        // Intentionally exclude previewBoardId / onBoardHover from deps: we
        // only want to react to isOver transitions, not to the parent's
        // preview-state echo (which would create a feedback loop).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOver, draggedItemId, isInterdepartmental, board.boardId]);

    return (
        <div
            ref={!isInterdepartmental ? setNodeRef : null}
            className={`board-sidebar-item ${selectedBoardId === board.boardId ? 'active' : ''} ${isOver ? 'drag-over' : ''} ${previewBoardId === board.boardId ? 'preview-active' : ''} ${isInterdepartmental ? 'not-droppable' : ''}`}
        >
            {children}
        </div>
    );
};

/**
 * BoardSidebar Component
 * A collapsible sidebar on the left side of the page that displays all available Kanban boards
 * and provides quick access to board management features.
 * Supports drag-and-drop functionality to move action items between boards.
 * 
 * @param {Object} props Component props
 * @param {boolean} props.isOpen - Whether the sidebar is currently open
 * @param {Function} props.onClose - Callback to close the sidebar
 * @param {Array} props.boards - Array of available boards
 * @param {number} props.selectedBoardId - Currently selected board ID
 * @param {Function} props.onBoardChange - Callback when board selection changes
 * @param {Function} props.onCreateBoard - Callback to create a new board
 * @param {Function} props.onBoardSettings - Callback to open board settings
 * @param {Function} props.onManageAccess - Callback to manage board access
 * @param {Function} props.onDuplicateBoard - Callback to duplicate a board
 * @param {Function} props.onDeleteBoard - Callback to delete a board
 * @param {number} props.draggedItemId - ID of currently dragged action item (null if not dragging)
 * @param {number} props.previewBoardId - ID of board to preview when hovering during drag
 * @param {Function} props.onBoardHover - Callback when hovering over a board during drag
 */
const BoardSidebar = ({
    isOpen,
    onClose,
    boards,
    selectedBoardId,
    onBoardChange,
    onCreateBoard,
    onBoardSettings,
    onManageAccess,
    onDuplicateBoard,
    onDeleteBoard,
    draggedItemId,
    previewBoardId,
    onBoardHover
}) => {
    const [expandedBoardId, setExpandedBoardId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const scrollContainerRef = useRef(null);
    const autoScrollRaf = useRef(null);

    /**
     * Auto-scroll the board list when dragging near the top or bottom edges.
     * Monitors pointer position while an item is being dragged and scrolls the
     * board-sidebar-content container accordingly.
     */
    const SCROLL_EDGE_PX = 60; // proximity zone in pixels
    const SCROLL_SPEED = 8;    // pixels per animation frame

    const handleAutoScroll = useCallback((e) => {
        if (!draggedItemId || !scrollContainerRef.current) return;

        const container = scrollContainerRef.current;
        const rect = container.getBoundingClientRect();
        const pointerY = e.clientY;

        // Cancel any pending frame
        if (autoScrollRaf.current) {
            cancelAnimationFrame(autoScrollRaf.current);
            autoScrollRaf.current = null;
        }

        const distFromTop = pointerY - rect.top;
        const distFromBottom = rect.bottom - pointerY;

        if (distFromTop < SCROLL_EDGE_PX && container.scrollTop > 0) {
            // Scroll up — speed increases the closer to the edge
            const factor = 1 - distFromTop / SCROLL_EDGE_PX;
            const step = () => {
                container.scrollTop -= Math.ceil(SCROLL_SPEED * factor);
            };
            autoScrollRaf.current = requestAnimationFrame(step);
        } else if (distFromBottom < SCROLL_EDGE_PX &&
                   container.scrollTop < container.scrollHeight - container.clientHeight) {
            // Scroll down
            const factor = 1 - distFromBottom / SCROLL_EDGE_PX;
            const step = () => {
                container.scrollTop += Math.ceil(SCROLL_SPEED * factor);
            };
            autoScrollRaf.current = requestAnimationFrame(step);
        }
    }, [draggedItemId]);

    useEffect(() => {
        if (!draggedItemId || !isOpen) return;
        // Use pointermove so it works while @dnd-kit holds the pointer capture
        const handler = (e) => handleAutoScroll(e);
        window.addEventListener('pointermove', handler);
        return () => {
            window.removeEventListener('pointermove', handler);
            if (autoScrollRaf.current) {
                cancelAnimationFrame(autoScrollRaf.current);
            }
        };
    }, [draggedItemId, isOpen, handleAutoScroll]);

    // Close sidebar when pointer leaves it during a drag
    const sidebarRef = useRef(null);
    useEffect(() => {
        if (!draggedItemId || !isOpen) return;
        const handlePointerMove = (e) => {
            if (!sidebarRef.current) return;
            const rect = sidebarRef.current.getBoundingClientRect();
            const outside =
                e.clientX < rect.left ||
                e.clientX > rect.right ||
                e.clientY < rect.top ||
                e.clientY > rect.bottom;
            if (outside) {
                onClose();
            }
        };
        window.addEventListener('pointermove', handlePointerMove);
        return () => window.removeEventListener('pointermove', handlePointerMove);
    }, [draggedItemId, isOpen, onClose]);

    /**
     * Filters boards based on search query
     */
    const filteredBoards = boards.filter(board => 
        board.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        board.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    /**
     * Groups boards by ownership status
     */
    const ownedBoards = filteredBoards.filter(b => b.isOwner);
    const sharedBoards = filteredBoards.filter(b => !b.isOwner);

    /**
     * Handles board selection
     */
    const handleBoardSelect = (boardId) => {
        onBoardChange(boardId);
        setExpandedBoardId(null);
    };

    /**
     * Toggles the expanded state for a board's actions menu
     */
    const toggleBoardExpanded = (e, boardId) => {
        e.stopPropagation();
        setExpandedBoardId(expandedBoardId === boardId ? null : boardId);
    };

    return (
        <>
            {/* Backdrop - shown when sidebar is open */}
            {isOpen && (
                <div 
                    className="board-sidebar-backdrop" 
                    onClick={onClose}
                />
            )}
            
            {/* Sidebar */}
            <div ref={sidebarRef} className={`board-sidebar ${isOpen ? 'open' : ''}`}>
                {/* Sidebar Header */}
                <div className="board-sidebar-header">
                    <h2>My Boards</h2>
                    <button 
                        className="btn-icon-close"
                        onClick={onClose}
                        title="Close sidebar"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Create Board Button */}
                <div className="board-sidebar-create">
                    <button 
                        className="btn-create-board"
                        onClick={() => {
                            onCreateBoard();
                            onClose();
                        }}
                    >
                        <FaPlus /> Create New Board
                    </button>
                </div>

                {/* Search Input */}
                <div className="board-sidebar-search">
                    <input
                        type="text"
                        placeholder="Search boards..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="board-search-input"
                    />
                </div>

                {/* Board List */}
                <div className="board-sidebar-content" ref={scrollContainerRef}>
                    {/* My Boards Section */}
                    {ownedBoards.length > 0 && (
                        <div className="board-section">
                            <h3 className="board-section-title">My Boards</h3>
                            <div className="board-list">
                                {ownedBoards.map(board => (
                                        <DroppableBoardItem
                                            key={board.boardId}
                                            board={board}
                                            selectedBoardId={selectedBoardId}
                                            previewBoardId={previewBoardId}
                                            draggedItemId={draggedItemId}
                                            onBoardHover={onBoardHover}
                                            sidebarOpen={isOpen}
                                        >
                                        <div 
                                            className="board-sidebar-item-content"
                                            onClick={() => handleBoardSelect(board.boardId)}
                                        >
                                            {/* Board Color Indicator */}
                                            {board.color && (
                                                <div 
                                                    className="board-color-bar"
                                                    style={{ backgroundColor: board.color }}
                                                />
                                            )}
                                            
                                            {/* Board Info */}
                                            <div className="board-info-container">
                                                <div className="board-name-row">
                                                        {board.icon && (
                                                            <span className="board-icon"><FaIcon icon={board.icon} /></span>
                                                        )}
                                                        <span className="board-name">{board.name}</span>
                                                    </div>
                                                    <div className="board-meta">
                                                        <span className="board-task-count">
                                                            {board.actionItemCount} {board.actionItemCount === 1 ? 'task' : 'tasks'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Expand Button */}
                                            <button
                                                className="btn-expand"
                                                onClick={(e) => toggleBoardExpanded(e, board.boardId)}
                                                title="Board actions"
                                            >
                                                <FaChevronRight className={expandedBoardId === board.boardId ? 'rotated' : ''} />
                                            </button>
                                        </div>

                                        {/* Expanded Actions Menu */}
                                        {expandedBoardId === board.boardId && (
                                            <div className="board-actions-menu">
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onBoardSettings(board);
                                                        onClose();
                                                    }}
                                                    className="board-action-btn"
                                                >
                                                    <FaCog /> Settings
                                                </button>
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDuplicateBoard(board);
                                                        onClose();
                                                    }}
                                                    className="board-action-btn"
                                                >
                                                    <FaCopy /> Duplicate
                                                </button>
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onManageAccess(board);
                                                        onClose();
                                                    }}
                                                    className="board-action-btn"
                                                >
                                                    <FaUsers /> Manage Access
                                                </button>
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (window.confirm(`Are you sure you want to delete "${board.name}"? This action cannot be undone.`)) {
                                                            onDeleteBoard(board.boardId);
                                                            onClose();
                                                        }
                                                    }}
                                                    className="board-action-btn danger"
                                                >
                                                    <FaTrash /> Delete
                                                </button>
                                            </div>
                                        )}
                                    </DroppableBoardItem>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Shared With Me Section */}
                    {sharedBoards.length > 0 && (
                        <div className="board-section">
                            <h3 className="board-section-title">Shared With Me</h3>
                            <div className="board-list">
                                {sharedBoards.map(board => (
                                    <DroppableBoardItem
                                        key={board.boardId}
                                        board={board}
                                        selectedBoardId={selectedBoardId}
                                        previewBoardId={previewBoardId}
                                        draggedItemId={draggedItemId}
                                        onBoardHover={onBoardHover}
                                        sidebarOpen={isOpen}
                                    >
                                        <div 
                                            className="board-sidebar-item-content"
                                            onClick={() => handleBoardSelect(board.boardId)}
                                        >
                                            {/* Board Color Indicator */}
                                            {board.color && (
                                                <div 
                                                    className="board-color-bar"
                                                    style={{ backgroundColor: board.color }}
                                                />
                                            )}
                                            
                                            {/* Board Info */}
                                            <div className="board-info-container">
                                                <div className="board-name-row">
                                                        {board.icon && (
                                                            <span className="board-icon"><FaIcon icon={board.icon} /></span>
                                                        )}
                                                        <span className="board-name">{board.name}</span>
                                                    </div>
                                                    <div className="board-meta">
                                                        <span className="board-permission">
                                                        {board.permissionLevel}
                                                    </span>
                                                    <span className="board-task-count">
                                                        · {board.actionItemCount} {board.actionItemCount === 1 ? 'task' : 'tasks'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Expand Button - For Admin permission or settings access */}
                                            {(board.permissionLevel === 'Admin' || board.hasSettingsAccess) && (
                                                <button
                                                    className="btn-expand"
                                                    onClick={(e) => toggleBoardExpanded(e, board.boardId)}
                                                    title="Board actions"
                                                >
                                                    <FaChevronRight className={expandedBoardId === board.boardId ? 'rotated' : ''} />
                                                </button>
                                            )}
                                        </div>

                                        {/* Expanded Actions Menu - For Admin permission or settings access */}
                                        {expandedBoardId === board.boardId && (board.permissionLevel === 'Admin' || board.hasSettingsAccess) && (
                                            <div className="board-actions-menu">
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onBoardSettings(board);
                                                        onClose();
                                                    }}
                                                    className="board-action-btn"
                                                >
                                                    <FaCog /> Settings
                                                </button>
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDuplicateBoard(board);
                                                        onClose();
                                                    }}
                                                    className="board-action-btn"
                                                >
                                                    <FaCopy /> Duplicate
                                                </button>
                                                {board.permissionLevel === 'Admin' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onManageAccess(board);
                                                            onClose();
                                                        }}
                                                        className="board-action-btn"
                                                    >
                                                        <FaUsers /> Manage Access
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </DroppableBoardItem>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Empty State */}
                    {filteredBoards.length === 0 && (
                        <div className="board-sidebar-empty">
                            {searchQuery ? (
                                <>
                                    <p>No boards found matching &quot;{searchQuery}&quot;</p>
                                    <button 
                                        className="btn-clear-search"
                                        onClick={() => setSearchQuery('')}
                                    >
                                        Clear Search
                                    </button>
                                </>
                            ) : (
                                <>
                                    <p>You don't have any boards yet.</p>
                                    <button 
                                        className="btn-create-board-empty"
                                        onClick={() => {
                                            onCreateBoard();
                                            onClose();
                                        }}
                                    >
                                        <FaPlus /> Create Your First Board
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BoardSidebar;
